export interface ViewportSideMenuProps {
  hasSelection: boolean;
  isFpsCamera: boolean;
  /** Sends a plain CommandsReceiver verb (camera, undo, redo, rotate). */
  onCommand: (command: string) => void;
  /** Deletes the selected instance — routes through RemoveInstance, not the verb. */
  onDeleteSelected: () => void;
}

interface ButtonDef {
  label: string;
  icon: string;
  /** "command" sends a verb; "delete" routes to onDeleteSelected. */
  kind: "command" | "delete";
  command?: string;
  requiresSelection?: boolean;
}

// Camera-mode toggle is handled separately (one button, two states); the rest
// of the buttons are flat verbs and live here in their fixed order below.
const BUTTONS: ButtonDef[] = [
  { label: "Undo", icon: "↶", kind: "command", command: "Undo" },
  { label: "Redo", icon: "↷", kind: "command", command: "Redo" },
  // Delete targets the selected instance by id (RemoveInstance), not the bare
  // Delete verb — the verb can't tell two copies of the same product apart.
  { label: "Delete", icon: "🗑", kind: "delete", requiresSelection: true },
  { label: "Rotate clockwise", icon: "↻", kind: "command", command: "RotateClockwise", requiresSelection: true },
  { label: "Rotate counter-clockwise", icon: "↺", kind: "command", command: "RotateCounterClockwise", requiresSelection: true },
];

/**
 * Vertical icon stack floating on the left edge of the viewport. Camera mode is
 * a single toggle: the icon shows the *current* mode, an active background
 * marks it on (matching how the camera mode reads as a state, not an action),
 * and clicking sends the inverse verb to switch.
 *
 * Selection-dependent buttons (Delete, Rotate*) stay visible-but-disabled when
 * nothing is selected so the toolbar doesn't reflow on selection changes.
 */
export function ViewportSideMenu({ hasSelection, isFpsCamera, onCommand, onDeleteSelected }: ViewportSideMenuProps) {
  const cameraLabel = isFpsCamera ? "FPS camera (click for top-down)" : "Top-down camera (click for FPS)";
  // 🚶 reads as a person navigating the scene; 🗺 reads as the overhead plan.
  const cameraIcon = isFpsCamera ? "🚶" : "🗺";
  const cameraTarget = isFpsCamera ? "EnableTopDownCamera" : "EnableFPSCamera";

  return (
    <div className="sd-sidemenu" role="toolbar" aria-label="Viewport actions">
      <button
        type="button"
        className="sd-sidemenu__btn sd-sidemenu__btn--active"
        onClick={() => onCommand(cameraTarget)}
        title={cameraLabel}
        aria-label={cameraLabel}
        aria-pressed={isFpsCamera}
      >
        <span aria-hidden="true">{cameraIcon}</span>
      </button>

      {BUTTONS.map(b => {
        const disabled = !!b.requiresSelection && !hasSelection;
        const key = b.kind === "delete" ? "delete" : b.command!;
        const handleClick = () => {
          if (disabled) return;
          if (b.kind === "delete") onDeleteSelected();
          else if (b.command) onCommand(b.command);
        };
        return (
          <button
            key={key}
            type="button"
            className={
              "sd-sidemenu__btn" +
              (disabled ? " sd-sidemenu__btn--disabled" : "")
            }
            onClick={handleClick}
            disabled={disabled}
            title={b.label}
            aria-label={b.label}
          >
            <span aria-hidden="true">{b.icon}</span>
          </button>
        );
      })}
    </div>
  );
}
